import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Modal, Input, Button, message } from "antd";
import { UserOutlined, CloseOutlined } from "@ant-design/icons";
import logoImg from "../../general/assets/logo/dealVoice.png";

const extractCompanyCode = (company) =>
  typeof company === "string" ? company : company?.companyCode || company?.companyName;

const getInitialCompanies = (collaboratingCompanies, companyCode) => {
  if (!Array.isArray(collaboratingCompanies) || collaboratingCompanies.length === 0) {
    return [];
  }
  return collaboratingCompanies
    .map(extractCompanyCode)
    .filter((code) => Boolean(code) && code !== companyCode);
};

const getInitialTags = (availableTags, hookTags) => {
  const pool = Array.isArray(availableTags) && availableTags.length > 0 ? availableTags : hookTags;
  if (Array.isArray(pool) && pool.length > 0) {
    return [pool[0]];
  }
  return [];
};

const generateSecureEmployeeCode = () => {
  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    const array = new Uint32Array(1);
    window.crypto.getRandomValues(array);
    const code = 100000 + (array[0] % 900000);
    return `DV${code}`;
  }
  return `DV${Date.now().toString().slice(-6)}`;
};

const EMPTY_ARRAY = [];

function AddEmployeeModal({ visible, onClose, onSave, editingEmployee, availableTags = EMPTY_ARRAY, collaboratingCompanies = EMPTY_ARRAY, companyCode = "" }) {
  const reduxCompanyTags = useSelector((state) => state.executor?.companyInfo?.tags || state.session?.settings?.tags) || EMPTY_ARRAY;
  const [name, setName] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [mobile, setMobile] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [allowedCompanies, setAllowedCompanies] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!visible) return;

    if (editingEmployee) {
      setName(editingEmployee.name || "");
      setCountryCode(editingEmployee.countryCode || "+91");
      setMobile(editingEmployee.mobile || "");
      setSelectedTags(Array.isArray(editingEmployee.tags) ? editingEmployee.tags : []);
      setAllowedCompanies(Array.isArray(editingEmployee.allowedCompanies) ? editingEmployee.allowedCompanies : []);
    } else {
      setName("");
      setCountryCode("+91");
      setMobile("");
      setSelectedTags(getInitialTags(availableTags, reduxCompanyTags));
      setAllowedCompanies(getInitialCompanies(collaboratingCompanies, companyCode));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, editingEmployee?._id, editingEmployee?.id]);

  const handleToggleTag = (tag) => {
    setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const handleToggleCompany = (code) => {
    setAllowedCompanies((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      message.warning("Please enter employee full name.");
      return;
    }
    const cleanMobile = mobile.replace(/\D/g, "").trim();
    if (!cleanMobile || cleanMobile.length < 7) {
      message.warning("Please enter a valid phone number.");
      return;
    }

    setSubmitting(true);
    try {
      await onSave({
        _id: editingEmployee?._id || editingEmployee?.id,
        name: name.trim(),
        mobile: cleanMobile,
        countryCode: countryCode || "+91",
        tags: selectedTags,
        allowedCompanies: allowedCompanies.filter((c) => c !== companyCode),
        employeeCode: editingEmployee?.employeeCode || generateSecureEmployeeCode(),
        deviceModel: editingEmployee?.deviceModel || "Device Unregistered",
      });
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const allCompanyCodes = useMemo(() => {
    const rawList = [...collaboratingCompanies.map(extractCompanyCode), ...allowedCompanies];
    return Array.from(new Set(rawList)).filter((code) => Boolean(code) && code !== companyCode);
  }, [collaboratingCompanies, allowedCompanies, companyCode]);

  const allTagList = useMemo(() => {
    const pool = [...(availableTags || []), ...(reduxCompanyTags || []), ...selectedTags];
    return Array.from(new Set(pool)).filter(Boolean);
  }, [availableTags, reduxCompanyTags, selectedTags]);

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      destroyOnClose
      width={460}
      styles={{
        content: {
          borderRadius: 20,
          padding: "28px 28px 24px 28px",
          boxShadow: "0 20px 40px -15px rgba(0,0,0,0.15)",
        },
      }}
    >
      <div style={{ position: "relative" }}>
        {/* Top Close Button */}
        <button
          type="button"
          onClick={onClose}
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            background: "#f1f5f9",
            border: "none",
            borderRadius: 8,
            width: 32,
            height: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "#64748b",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#e2e8f0";
            e.currentTarget.style.color = "#0f172a";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#f1f5f9";
            e.currentTarget.style.color = "#64748b";
          }}
        >
          <CloseOutlined style={{ fontSize: 13 }} />
        </button>

        {/* Brand Logo Header */}
        <div style={{ marginBottom: 20 }}>
          <img src={logoImg} alt="dealVoice™" style={{ height: 32, objectFit: "contain", display: "block" }} />
        </div>

        {/* Title and Subtitle */}
        <div style={{ marginBottom: 22 }}>
          <div
            style={{
              fontSize: "1.65rem",
              fontWeight: 700,
              color: "#0f172a",
              margin: "0 0 6px 0",
              letterSpacing: "-0.02em",
            }}
          >
            {editingEmployee ? "Edit Employee" : "Add Employee"}
          </div>
          <p style={{ fontSize: "0.92rem", color: "#64748b", margin: 0 }}>{editingEmployee ? "Update employee name, phone, and tags." : "Add employee name, phone, and tags."}</p>
        </div>

        {/* Form Fields */}
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Field: Full Name */}
          <div>
            <label
              htmlFor="employee-full-name"
              style={{
                display: "block",
                fontSize: "0.85rem",
                fontWeight: 500,
                color: "#475569",
                marginBottom: 6,
              }}
            >
              Full Name
            </label>
            <Input
              id="employee-full-name"
              prefix={<UserOutlined style={{ color: "#94a3b8", fontSize: 16, marginRight: 6 }} />}
              placeholder="e.g. Rooshin Paul"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{
                height: 44,
                borderRadius: 10,
                borderColor: "#cbd5e1",
                fontSize: "0.95rem",
                color: "#0f172a",
              }}
            />
          </div>

          {/* Field: Phone Number */}
          <div>
            <label
              htmlFor="employee-phone-number"
              style={{
                display: "block",
                fontSize: "0.85rem",
                fontWeight: 500,
                color: "#475569",
                marginBottom: 6,
              }}
            >
              Phone Number
            </label>
            <div style={{ display: "flex", alignItems: "stretch" }}>
              <div
                style={{
                  width: 90,
                  height: 44,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  border: "1px solid #cbd5e1",
                  borderRight: "none",
                  borderRadius: "10px 0 0 10px",
                  background: "#ffffff",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  color: "#1e293b",
                  userSelect: "none",
                }}
              >
                <span>🇮🇳</span>
                <span>{countryCode}</span>
              </div>
              <Input
                id="employee-phone-number"
                placeholder="9787582712"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                style={{
                  flex: 1,
                  height: 44,
                  borderRadius: "0 10px 10px 0",
                  borderColor: "#cbd5e1",
                  fontSize: "0.95rem",
                  color: "#0f172a",
                }}
              />
            </div>
          </div>

          {/* Field: Selected Tags */}
          {allTagList.length > 0 && (
            <div>
              <div
                style={{
                  display: "block",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  color: "#475569",
                  marginBottom: 8,
                }}
              >
                Selected Tags
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {allTagList.map((tag) => {
                  const isSelected = selectedTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => handleToggleTag(tag)}
                      style={{
                        background: isSelected ? "#eef2ff" : "#ffffff",
                        border: isSelected ? "1.5px solid #6366f1" : "1px solid #e2e8f0",
                        color: isSelected ? "#4338ca" : "#475569",
                        fontWeight: isSelected ? 600 : 500,
                        padding: "6px 14px",
                        borderRadius: 8,
                        cursor: "pointer",
                        fontSize: "0.85rem",
                        transition: "all 0.15s ease",
                        outline: "none",
                      }}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Field: Allowed Collaborating Companies */}
          {allCompanyCodes.length > 0 && (
            <div>
              <div
                style={{
                  display: "block",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  color: "#475569",
                  marginBottom: 2,
                }}
              >
                Allowed Collaborating Companies
              </div>
              <p
                style={{
                  fontSize: "0.78rem",
                  color: "#64748b",
                  margin: "0 0 8px 0",
                }}
              >
                Select companies this employee can generate quotes/invoices for.
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {allCompanyCodes.map((code) => {
                  const isSelected = allowedCompanies.includes(code);
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => handleToggleCompany(code)}
                      style={{
                        background: isSelected ? "#eef2ff" : "#ffffff",
                        border: isSelected ? "1.5px solid #6366f1" : "1px solid #e2e8f0",
                        color: isSelected ? "#4338ca" : "#475569",
                        fontWeight: isSelected ? 600 : 500,
                        padding: "6px 14px",
                        borderRadius: 8,
                        cursor: "pointer",
                        fontSize: "0.85rem",
                        transition: "all 0.15s ease",
                        outline: "none",
                      }}
                    >
                      {code}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Primary Action Button */}
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={submitting}
            style={{
              width: "100%",
              height: 46,
              borderRadius: 10,
              background: "#1d4ed8",
              borderColor: "#1d4ed8",
              fontSize: "1rem",
              fontWeight: 600,
              marginTop: 6,
            }}
          >
            {editingEmployee ? "Save Changes" : "Add Employee"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default AddEmployeeModal;
